import { logError } from './logging';

/**
 * Encodes personalisation selections into a URL-safe string.
 * Excludes IMAGE_UPLOAD slots as they are not stored for sharing purposes.
 * @param {Object} options - Options object.
 * @param {Object} options.selections - The selections object to encode.
 * @returns {string} - Base64 encoded string of the selections.
 */
export const encodePersonalisationSelections = ({ selections }) => {
  try {
    // Filter out slots that shouldn't be shared
    const filteredSelections = {
      p: selections.productVariantId, // productVariantId
      s: selections.slots // slots
        .filter(slot =>
          slot.selected !== null &&
          slot.selected !== undefined &&
          slot.slotType !== 'IMAGE_UPLOAD'
        )
        .map(slot => ({
          i: slot.id, // id
          s: slot.selected, // selected
          t: slot.slotType, // slotType
          st: slot.stepId, // stepId
          l: slot.lineItemPropertyKey, // lineItemPropertyKey
          tl: slot.translatedLineItemPropertyKey // translatedLineItemPropertyKey
        }))
    };

    const jsonString = JSON.stringify(filteredSelections);
    return btoa(jsonString);
  } catch (error) {
    logError({
      label: 'encodePersonalisationSelections',
      error,
      message: 'Failed to encode personalisation selections',
      extraData: { selections }
    });
    return '';
  }
};

/**
 * Decodes a URL-safe string back into personalisation selections.
 * @param {Object} options - Options object.
 * @param {string} options.encodedString - The Base64 encoded string to decode.
 * @returns {Object|null} - The decoded selections object or null if decoding fails.
 */
export const decodePersonalisationSelections = ({ encodedString }) => {
  try {
    if (!encodedString || typeof encodedString !== 'string') {
      return null;
    }

    const jsonString = atob(encodedString);
    const selections = JSON.parse(jsonString);

    // Basic validation
    if (!selections || typeof selections !== 'object') {
      return null;
    }

    // Convert shortened field names back to full names
    const expandedSelections = {
      productVariantId: selections.p,
      slots: selections.s?.map(slot => ({
        id: slot.i,
        selected: slot.s,
        slotType: slot.t,
        stepId: slot.st,
        lineItemPropertyKey: slot.l,
        translatedLineItemPropertyKey: slot.tl
      })) || []
    };

    return expandedSelections;
  } catch (error) {
    logError({
      label: 'decodePersonalisationSelections',
      error,
      message: 'Failed to decode personalisation selections',
      extraData: { encodedString }
    });
    return null;
  }
};

/**
 * Generates a URL with encoded personalisation selections.
 * @param {Object} options - Options object.
 * @param {string} options.baseUrl - The base URL to append the parameter to.
 * @param {Object} options.selections - The selections object to encode.
 * @returns {string} - The complete URL with personalisation parameter.
 */
export const generatePersonalisationUrl = ({ baseUrl, selections }) => {
  try {
    const encodedSelections = encodePersonalisationSelections({ selections });

    if (!encodedSelections) {
      return baseUrl;
    }

    const url = new URL(baseUrl);
    url.searchParams.set('personalisation', encodedSelections);
    // Ensure the personalisation drawer auto-opens when visiting the shared URL.
    url.searchParams.set('openPersonalisation', 'true');
    return url.toString();
  } catch (error) {
    logError({
      label: 'generatePersonalisationUrl',
      error,
      message: 'Failed to generate personalisation URL',
      extraData: { baseUrl, selections }
    });
    return baseUrl;
  }
};

/**
 * Extracts personalisation selections from URL parameters.
 * @param {Object} options - Options object.
 * @param {string} [options.url] - The URL to extract from. Defaults to current window location.
 * @returns {Object|null} - The decoded selections object or null if not found.
 */
export const extractPersonalisationFromUrl = ({ url = window.location.href } = {}) => {
  try {
    const urlObj = new URL(url);
    const encodedSelections = urlObj.searchParams.get('personalisation');

    if (!encodedSelections) {
      return null;
    }

    return decodePersonalisationSelections({ encodedString: encodedSelections });
  } catch (error) {
    logError({
      label: 'extractPersonalisationFromUrl',
      error,
      message: 'Failed to extract personalisation from URL',
      extraData: { url }
    });
    return null;
  }
};

/**
 * Removes personalisation-related query parameters from the current URL without refreshing the page.
 * Uses replaceState to avoid adding a new history entry.
 */
export const clearPersonalisationFromUrl = () => {
  try {
    const url = new URL(window.location.href);
    
    // Remove personalisation-related parameters.
    url.searchParams.delete('personalisation');
    url.searchParams.delete('openPersonalisation');
    
    // Build the new URL string, preserving hash if present.
    const newUrl = url.pathname + (url.search ? url.search : '') + (url.hash || '');
    
    // Update the URL without refreshing the page or adding a history entry.
    window.history.replaceState({}, '', newUrl);
  } catch (error) {
    logError({
      label: 'clearPersonalisationFromUrl',
      error,
      message: 'Failed to clear personalisation parameters from URL',
      extraData: { url: window.location.href }
    });
  }
};
